<template>
  <div>
    <!-- Header with Add Button -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-btn color="primary" @click="showCreateDialog = true">
          <v-icon left>mdi-plus</v-icon>
          Add New Cost Category
        </v-btn>
      </v-col>
      <v-col cols="12" md="6" class="text-right">
        <v-btn
          color="info"
          outlined
          @click="refreshCategories"
          :loading="costCategoriesLoading"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="costCategories"
          :loading="costCategoriesLoading"
          :sort-by="['calculation_order']"
          class="elevation-1"
          item-key="_id"
        >
          <!-- Category Code with Icon -->
          <template #item.category_code="{ item }">
            <v-chip
              :color="getCategoryColor(item.category_code)"
              text-color="white"
              small
            >
              <v-icon left small>{{ getCategoryIcon(item.category_code) }}</v-icon>
              {{ item.category_code }}
            </v-chip>
          </template>

          <!-- Category Name -->
          <template #item.category_name="{ item }">
            <span class="font-weight-medium">{{ item.category_name }}</span>
          </template>

          <!-- Base Charge -->
          <template #item.is_base_charge="{ item }">
            <v-chip
              :color="item.is_base_charge ? 'blue' : 'green'"
              text-color="white"
              x-small
            >
              <v-icon left x-small>
                {{ item.is_base_charge ? 'mdi-currency-usd' : 'mdi-percent' }}
              </v-icon>
              {{ item.is_base_charge ? 'Base' : 'Percentage' }}
            </v-chip>
          </template>

          <!-- Status -->
          <template #item.is_active="{ item }">
            <v-chip
              :color="item.is_active ? 'success' : 'error'"
              text-color="white"
              x-small
            >
              <v-icon left x-small>
                {{ item.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
              {{ item.is_active ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template #item.actions="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="viewCategory(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>View Details</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="warning"
                  @click="editCategory(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit Category</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  @click="confirmDeleteCategory(item)"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!item.is_active"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>
                {{
                  item.is_active
                    ? 'Deactivate Category'
                    : 'Cannot deactivate inactive category'
                }}
              </span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">
            {{ editingCategory ? 'Edit Cost Category' : 'Create New Cost Category' }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-form
            id="cost-category-form"
            ref="form"
            v-model="valid"
            @submit.prevent="saveCategory"
          >
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="categoryForm.category_code"
                  label="Category Code *"
                  :rules="codeRules"
                  :disabled="editingCategory && editCategory._id"
                  outlined
                  hint="Unique identifier (e.g., KWH_FIXED_CHARGE)"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="categoryForm.category_name"
                  label="Category Name *"
                  :rules="nameRules"
                  outlined
                  hint="Display name (e.g., kWh Fixed Charge)"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="categoryForm.description"
                  label="Description"
                  outlined
                  rows="3"
                  hint="Optional description of this cost category"
                  persistent-hint
                ></v-textarea>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="categoryForm.is_base_charge"
                  label="Base Charge"
                  :hint="baseChargeHint"
                  persistent-hint
                  @change="onChargeTypeChange"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="categoryForm.calculation_order"
                  label="Order"
                  type="number"
                  :rules="orderRules"
                  outlined
                  hint="Order for calculation (lower numbers first)"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Percentage Applies To Section (only for percentage charges) -->
            <v-row v-if="!categoryForm.is_base_charge">
              <v-col cols="12">
                <v-select
                  v-model="categoryForm.percentage_applies_to"
                  :items="baseChargeOptions"
                  label="Percentage Applies To"
                  multiple
                  chips
                  deletable-chips
                  outlined
                  :hint="percentageAppliesHint"
                  persistent-hint
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="index < 2"
                      :key="item.value || item"
                      :color="getCategoryColor(item.value || item)"
                      text-color="white"
                      small
                      close
                      @click:close="removeAppliesTo(item.value || item)"
                    >
                      <v-icon left small>{{ getCategoryIcon(item.value || item) }}</v-icon>
                      {{ item.value || item }}
                    </v-chip>
                    <span
                      v-if="index === 2"
                      class="grey--text caption"
                    >
                      (+{{ categoryForm.percentage_applies_to.length - 2 }} others)
                    </span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-action>
                      <v-checkbox
                        :input-value="
                          categoryForm.percentage_applies_to.includes(item.value || item)
                        "
                      ></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-chip
                          :color="getCategoryColor(item.value || item)"
                          text-color="white"
                          small
                        >
                          <v-icon left small>{{ getCategoryIcon(item.value || item) }}</v-icon>
                          {{ item.value || item }}
                        </v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="categoryForm.is_active"
                  label="Active"
                  hint="Only active categories can be used for billing rates"
                  persistent-hint
                ></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">
            Cancel
          </v-btn>
          <v-btn
            type="submit"
            form="cost-category-form"
            color="primary"
            :disabled="!valid"
            :loading="saving"
          >
            {{ editingCategory ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Action</v-card-title>
        <v-card-text>
          Choose what you want to do with the cost category
          <strong>{{ selectedCategory ? selectedCategory.category_name : '' }}</strong>?
          <br><br>
          <v-alert type="warning" dense>
            Deactivate marks the category inactive. Delete removes the record.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" text @click="deleteCategoryConfirmed">
            Delete
          </v-btn>
          <v-btn color="warning" text @click="deactivateCategoryConfirmed">
            Deactivate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="600px">
      <v-card v-if="selectedCategory">
        <v-card-title>
          <span class="headline">Cost Category Details</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Category Code</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      :color="getCategoryColor(selectedCategory.category_code)"
                      text-color="white"
                      small
                    >
                      {{ selectedCategory.category_code }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Category Name</v-list-item-title>
                  <v-list-item-subtitle class="text-h6">
                    {{ selectedCategory.category_name }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Description</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ selectedCategory.description || 'No description provided' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Charge Type</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      :color="selectedCategory.is_base_charge ? 'blue' : 'green'"
                      text-color="white"
                      small
                    >
                      {{
                        selectedCategory.is_base_charge
                          ? 'Base Charge'
                          : 'Percentage Charge'
                      }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Calculation Order</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ selectedCategory.calculation_order }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Status</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      :color="selectedCategory.is_active ? 'success' : 'error'"
                      text-color="white"
                      small
                    >
                      {{ selectedCategory.is_active ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col
              v-if="
                !selectedCategory.is_base_charge
                  && selectedCategory.percentage_applies_to
                  && selectedCategory.percentage_applies_to.length > 0
              "
              cols="12"
            >
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Percentage Applies To</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      v-for="categoryCode in normalizePercentageAppliesTo(
                        selectedCategory.percentage_applies_to
                      )"
                      :key="categoryCode"
                      :color="getCategoryColor(categoryCode)"
                      text-color="white"
                      small
                      class="mr-1 mb-1"
                    >
                      <v-icon left small>{{ getCategoryIcon(categoryCode) }}</v-icon>
                      {{ categoryCode }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="editCategory(selectedCategory)">
            Edit Category
          </v-btn>
          <v-btn text @click="detailsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { CostCategoriesService } from '../../services';
import costCategoryMixin from '../../mixins/costCategoryMixin';
import { normalizePercentageAppliesTo } from '../../utils/chargeMaster';

export default {
  name: 'CostCategoryList',
  mixins: [costCategoryMixin],
  data() {
    return {
      headers: [
        { text: 'Category Code', value: 'category_code', sortable: true },
        { text: 'Category Name', value: 'category_name', sortable: true },
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Charge Type', value: 'is_base_charge', sortable: true },
        { text: 'Order', value: 'calculation_order', sortable: true },
        { text: 'Status', value: 'is_active', sortable: true },
        {
          text: 'Actions', value: 'actions', sortable: false, width: '150px',
        },
      ],
      showCreateDialog: false,
      detailsDialog: false,
      deleteDialog: false,
      selectedCategory: null,
      editingCategory: null,
      valid: false,
      saving: false,
      baseChargeHint: 'Base charges are calculated first; percentage charges use the base total',
      percentageAppliesHint: 'Select base charges to use, or leave empty to apply to all base charges',
      categoryForm: {
        category_code: '',
        category_name: '',
        description: '',
        is_base_charge: true,
        calculation_order: 1,
        is_active: true,
        percentage_applies_to: [],
      },
    };
  },

  computed: {
    ...mapGetters('billingApp', ['allCostCategories']),

    costCategories() {
      return this.allCostCategories;
    },

    codeRules() {
      return [
        (v) => !!v || 'Category code is required',
        (v) => /^[A-Z_]+$/.test(v) || 'Category code must contain only uppercase letters and underscores',
        (v) => !this.isDuplicateCode(v) || 'Category code already exists',
      ];
    },

    nameRules() {
      return [
        (v) => !!v || 'Category name is required',
        (v) => v.length >= 3 || 'Category name must be at least 3 characters',
      ];
    },

    orderRules() {
      return [
        (v) => (v !== null && v !== '') || 'Calculation order is required',
        (v) => !Number.isNaN(Number(v)) || 'Calculation order must be a number',
        (v) => v >= 1 || 'Calculation order must be at least 1',
      ];
    },

    baseChargeOptions() {
      return this.costCategories
        .filter((cat) => (
          cat.is_active
          && cat.category_code !== this.categoryForm.category_code
        ))
        .map((cat) => ({
          text: `${cat.category_name} (${cat.is_base_charge ? 'Base' : 'Percentage'})`,
          value: cat.category_code,
        }));
    },
  },

  async created() {
    // Override mixin's created hook to load all categories instead of just active ones
    await this.loadAllCostCategories();
  },

  methods: {
    async refreshCategories() {
      await this.loadAllCostCategories();
      this.$store.dispatch('showSnackbar', {
        message: 'Cost categories refreshed successfully',
        color: 'success',
      });
    },

    /**
     * Loads all cost categories (including inactive ones) for management
     */
    async loadAllCostCategories() {
      if (!CostCategoriesService) {
        return;
      }

      try {
        await this.$store.dispatch('billingApp/fetchCostCategories');
      } catch (error) {
        console.error('❌ Error loading cost categories:', error);
      }
    },

    viewCategory(category) {
      this.selectedCategory = category;
      this.detailsDialog = true;
    },

    editCategory(category) {
      this.editingCategory = category;
      this.categoryForm = {
        category_code: category.category_code,
        category_name: category.category_name,
        description: category.description || '',
        is_base_charge: category.is_base_charge,
        calculation_order: category.calculation_order,
        is_active: category.is_active,
        percentage_applies_to: this.normalizePercentageAppliesTo(category.percentage_applies_to),
        _id: category._id,
      };
      this.showCreateDialog = true;
      this.detailsDialog = false;
    },

    confirmDeleteCategory(category) {
      this.selectedCategory = category;
      this.deleteDialog = true;
    },

    async deactivateCategoryConfirmed() {
      try {
        await CostCategoriesService.updateCategory(this.selectedCategory._id, {
          ...this.selectedCategory,
          is_active: false,
        });

        this.deleteDialog = false;
        this.selectedCategory = null;
        await this.loadAllCostCategories();

        this.$store.dispatch('showSnackbar', {
          message: 'Cost category deactivated successfully',
          color: 'success',
        });
      } catch (error) {
        this.$store.dispatch('showSnackbar', {
          message: `Error deactivating category: ${error.message}`,
          color: 'error',
        });
      }
    },

    async deleteCategoryConfirmed() {
      try {
        await CostCategoriesService.hardDeleteCategory(this.selectedCategory._id);

        this.deleteDialog = false;
        this.selectedCategory = null;
        await this.loadAllCostCategories();

        this.$store.dispatch('showSnackbar', {
          message: 'Cost category deleted successfully',
          color: 'success',
        });
      } catch (error) {
        this.$store.dispatch('showSnackbar', {
          message: `Error deleting category: ${error.message}`,
          color: 'error',
        });
      }
    },

    async saveCategory() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        if (this.editingCategory) {
          await CostCategoriesService.updateCategory(
            this.categoryForm._id,
            this.getCategoryPayload(),
          );
          this.$store.dispatch('showSnackbar', {
            message: 'Cost category updated successfully',
            color: 'success',
          });
        } else {
          await CostCategoriesService.createCategory(this.getCategoryPayload());
          this.$store.dispatch('showSnackbar', {
            message: 'Cost category created successfully',
            color: 'success',
          });
        }

        this.closeDialog();
        await this.loadAllCostCategories();
      } catch (error) {
        console.error('❌ Error saving category:', error);
        this.$store.dispatch('showSnackbar', {
          message: `Error saving category: ${error.message}`,
          color: 'error',
        });
      } finally {
        this.saving = false;
      }
    },

    closeDialog() {
      this.showCreateDialog = false;
      this.editingCategory = null;
      this.categoryForm = {
        category_code: '',
        category_name: '',
        description: '',
        is_base_charge: true,
        calculation_order: 1,
        is_active: true,
        percentage_applies_to: [],
      };
      this.$refs.form?.resetValidation();
    },

    isDuplicateCode(code) {
      if (this.editingCategory && this.editingCategory.category_code === code) {
        return false; // Allow keeping the same code when editing
      }
      return this.costCategories.some((cat) => cat.category_code === code);
    },

    onChargeTypeChange() {
      // Clear percentage_applies_to when switching to base charge
      if (this.categoryForm.is_base_charge) {
        this.categoryForm.percentage_applies_to = [];
      }
    },

    normalizePercentageAppliesTo(value) {
      return normalizePercentageAppliesTo(value);
    },

    getCategoryPayload() {
      return {
        ...this.categoryForm,
        percentage_applies_to: this.categoryForm.is_base_charge
          ? '[]'
          : JSON.stringify(
            this.normalizePercentageAppliesTo(this.categoryForm.percentage_applies_to),
          ),
      };
    },

    removeAppliesTo(categoryCode) {
      const index = this.categoryForm.percentage_applies_to.indexOf(categoryCode);
      if (index > -1) {
        this.categoryForm.percentage_applies_to.splice(index, 1);
      }
    },
  },
};
</script>
